import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Check, Copy } from "lucide-react";
import { toast } from "sonner";
import { CONTACT_EMAIL } from "@/data/site";
import { LogoMark } from "@/components/Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
    "w-full bg-transparent border-b border-white/15 focus:border-[#FF3333] outline-none py-3 text-sm text-neutral-200 placeholder:text-neutral-600 transition-colors duration-300";

const fade = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.16, 1, 0.3, 1] } },
};

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", project: "", budget: "", message: "" });
    const [sending, setSending] = useState(false);
    const [copied, setCopied] = useState(false);

    const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(CONTACT_EMAIL);
            setCopied(true);
            toast.success("Email copied to clipboard");
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error("Could not copy - email us at " + CONTACT_EMAIL);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/contact`, {
                name: form.name,
                email: form.email,
                project: form.project || null,
                budget: form.budget || null,
                message: form.message,
            });
            toast.success("Enquiry sent - we will be in touch shortly.");
            setForm({ name: "", email: "", project: "", budget: "", message: "" });
        } catch {
            toast.error("Could not send right now - email us directly at " + CONTACT_EMAIL);
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contact" data-testid="contact-section" className="relative border-t border-white/5 px-5 sm:px-10 pt-24 sm:pt-36 pb-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 lg:gap-24">
                <motion.div variants={fade} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-100px" }}>
                    <p className="font-mono-x text-[10px] sm:text-xs tracking-[0.4em] text-[#FF3333] mb-6">
                        START A PROJECT
                    </p>
                    <h2 className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tighter leading-[0.9]">
                        Let's create the{" "}
                        <span className="text-outline">impossible</span>
                    </h2>
                    <button
                        data-testid="contact-copy-email-button"
                        onClick={copyEmail}
                        className="group mt-10 flex items-center gap-3 text-left"
                    >
                        <span className="font-mono-x text-sm sm:text-base tracking-[0.15em] text-neutral-200 border-b border-[#FF3333]/60 pb-1 group-hover:text-[#FF3333] transition-colors">
                            {CONTACT_EMAIL}
                        </span>
                        {copied ? <Check size={16} className="text-[#FF3333]" /> : <Copy size={16} className="text-neutral-500 group-hover:text-[#FF3333] transition-colors" />}
                    </button>
                    <p className="font-mono-x text-[10px] tracking-[0.3em] text-neutral-500 mt-10">
                        ATLANTA - GEORGIA &nbsp;/&nbsp; NEW YORK - NEW YORK &nbsp;/&nbsp; INDONESIA
                    </p>
                </motion.div>

                <motion.form
                    data-testid="contact-form"
                    variants={fade}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-100px" }}
                    transition={{ delay: 0.15 }}
                    onSubmit={submit}
                    className="flex flex-col gap-7"
                >
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                        <input
                            data-testid="contact-input-name"
                            required
                            value={form.name}
                            onChange={update("name")}
                            placeholder="NAME"
                            className={`${inputClass} font-mono-x text-xs tracking-[0.2em] uppercase`}
                        />
                        <input
                            data-testid="contact-input-email"
                            required
                            type="email"
                            value={form.email}
                            onChange={update("email")}
                            placeholder="EMAIL"
                            className={`${inputClass} font-mono-x text-xs tracking-[0.2em] uppercase`}
                        />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
                        <input
                            data-testid="contact-input-project"
                            value={form.project}
                            onChange={update("project")}
                            placeholder="PROJECT TITLE"
                            className={`${inputClass} font-mono-x text-xs tracking-[0.2em] uppercase`}
                        />
                        <select
                            data-testid="contact-input-budget"
                            value={form.budget}
                            onChange={update("budget")}
                            className={`${inputClass} font-mono-x text-xs tracking-[0.2em] uppercase bg-[#050505] ${form.budget ? "text-neutral-200" : "text-neutral-600"}`}
                        >
                            <option value="">BUDGET TIER</option>
                            <option value="indie">INDIE / PASSION PROJECT</option>
                            <option value="mid">MID-BUDGET FEATURE / EPISODIC</option>
                            <option value="studio">LARGE-BUDGET / STUDIO</option>
                            <option value="tbd">NOT SURE YET</option>
                        </select>
                    </div>
                    <textarea
                        data-testid="contact-input-message"
                        required
                        rows={4}
                        value={form.message}
                        onChange={update("message")}
                        placeholder="TELL US ABOUT THE SHOTS YOU NEED"
                        className={`${inputClass} font-mono-x text-xs tracking-[0.2em] uppercase resize-none`}
                    />
                    <button
                        data-testid="contact-submit-button"
                        type="submit"
                        disabled={sending}
                        className="group mt-2 flex w-fit items-center gap-3 border border-white/20 px-8 py-4 font-mono-x text-[11px] tracking-[0.35em] transition-all duration-500 hover:border-[#FF3333] hover:bg-[#FF3333] hover:shadow-[0_0_30px_rgba(255,51,51,0.35)] hover:-translate-y-0.5 active:scale-95 disabled:opacity-40"
                    >
                        {sending ? "SENDING..." : "SEND ENQUIRY"}
                        <ArrowUpRight size={14} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </button>
                </motion.form>
            </div>

            <footer className="mt-24 sm:mt-32 flex flex-col sm:flex-row items-center justify-between gap-6 border-t border-white/5 pt-8">
                <div className="flex items-center gap-3">
                    <LogoMark size={16} />
                    <span className="font-mono-x text-[10px] tracking-[0.35em] text-neutral-500">
                        EXODUS FX - BOUTIQUE VFX STUDIO
                    </span>
                </div>
                <p className="font-mono-x text-[9px] tracking-[0.3em] text-neutral-600">
                    &copy; 2026 EXODUS FX - ALL FRAMES CRAFTED WITH OBSESSION
                </p>
            </footer>
        </section>
    );
}
